import { cloneDeep } from 'lodash';

import { goalSettingsActionTypes } from './constants';
import {
	setExtraProperties,
	setSelectedCategory,
	setSelectedDuration,
	setSelectedSubcategory,
	setSelectedSubject,
} from '../helpers';

import type {
	GoalSettingsAction,
	GoalSettingsState,
	Subject,
	UnsavedChangesModalState,
} from '../types';

const initialState: GoalSettingsState = {
	loadingGoalSettings: false,
	loadingDurations: false,
	loadingSave: false,
	subjects: [],
	initialSubjects: [],
	durations: [],
	initialDurations: [],
	playDaysPerWeek: 0,
	initialPlayDaysPerWeek: null,
	currentProgress: 3,
	unsavedChangesModal: {
		open: false,
		submitCallback: null,
	},
	playMode: null,
	initialPlayMode: null,
	activeEditStep: null,
	activeEditSubStep: null,
};

export default function goalSettingsReducer(
	state: GoalSettingsState = initialState,
	action: GoalSettingsAction,
): GoalSettingsState {
	const { type, payload } = action;
	const {
		categoryId,
		data,
		duration,
		durations,
		playDaysPerWeek,
		playMode,
		progress,
		selected,
		step,
		subjectId,
		subcategoryId,
		subStep,
	} = payload || {};

	switch (type) {
		case goalSettingsActionTypes.GET_GOAL_SETTINGS_REQUEST:
			return {
				...state,
				loadingGoalSettings: true,
			};

		case goalSettingsActionTypes.GET_GOAL_DURATIONS_REQUEST:
			return {
				...state,
				loadingDurations: true,
			};

		case goalSettingsActionTypes.GET_GOAL_SETTINGS_SUCCESS:
			const customSubjects = setExtraProperties(data as Subject[]);
			return {
				...state,
				initialSubjects: cloneDeep(customSubjects),
				subjects: customSubjects,
				loadingGoalSettings: false,
				initialPlayMode: state.playMode ?? null,
			};

		case goalSettingsActionTypes.GET_GOAL_DURATIONS_SUCCESS:
			const customDurations = durations!;
			return {
				...state,
				initialDurations: cloneDeep(customDurations),
				durations: customDurations,
				loadingDurations: false,
			};

		case goalSettingsActionTypes.GET_GOAL_SETTINGS_ERROR:
			return {
				...state,
				loadingGoalSettings: false,
			};

		case goalSettingsActionTypes.GET_GOAL_DURATIONS_ERROR:
			return {
				...state,
				loadingDurations: false,
			};

		case goalSettingsActionTypes.SET_SELECTED_SUBJECT:
			const subjects = setSelectedSubject(subjectId!, selected!, state.subjects);
			return {
				...state,
				subjects,
			};

		case goalSettingsActionTypes.SET_SELECTED_CATEGORY:
			return {
				...state,
				subjects: setSelectedCategory(subjectId!, categoryId!, selected!, state.subjects),
			};

		case goalSettingsActionTypes.SET_SELECTED_SUBCATEGORY:
			return {
				...state,
				subjects: setSelectedSubcategory(
					subjectId!,
					categoryId!,
					subcategoryId!,
					selected!,
					state.subjects,
				),
			};

		case goalSettingsActionTypes.SET_SELECTED_DURATION:
			return {
				...state,
				durations: setSelectedDuration(duration!, state.durations),
			};

		case goalSettingsActionTypes.SET_CURRENT_PROGRESS:
			return {
				...state,
				currentProgress: progress!,
			};

		case goalSettingsActionTypes.SAVE_GOAL_SETTING_REQUEST:
			return {
				...state,
				loadingSave: true,
			};

		case goalSettingsActionTypes.SAVE_GOAL_SETTING_SUCCESS:
		case goalSettingsActionTypes.SAVE_GOAL_SETTING_ERROR:
			return {
				...state,
				loadingSave: false,
			};

		case goalSettingsActionTypes.TOGGLE_UNSAVED_CHANGES_MODAL:
			return {
				...state,
				unsavedChangesModal: payload!.data as UnsavedChangesModalState,
			};

		case goalSettingsActionTypes.SET_PLAY_DAYS_PER_WEEK:
			return {
				...state,
				playDaysPerWeek: playDaysPerWeek!,
			};

		case goalSettingsActionTypes.GET_CURRENT_PRACTICE_SUCCESS:
			return {
				...state,
				playDaysPerWeek: playDaysPerWeek!,
				initialPlayDaysPerWeek: playDaysPerWeek!,
			};

		case goalSettingsActionTypes.SET_PLAY_MODE:
			return {
				...state,
				playMode: playMode ?? null,
				initialPlayMode: state.initialPlayMode ?? playMode ?? null,
			};

		case goalSettingsActionTypes.SET_ACTIVE_EDIT_TARGET:
			return {
				...state,
				activeEditStep: step ?? null,
				activeEditSubStep: subStep ?? null,
			};

		case goalSettingsActionTypes.RESET_SUBJECTS_TO_INITIAL:
			return {
				...state,
				subjects: cloneDeep(state.initialSubjects),
			};

		case goalSettingsActionTypes.RESET_PRACTICE_TO_INITIAL:
			return {
				...state,
				durations: cloneDeep(state.initialDurations),
				playDaysPerWeek: state.initialPlayDaysPerWeek ?? 0,
			};

		case goalSettingsActionTypes.RESET_PLAY_MODE_TO_INITIAL:
			return {
				...state,
				playMode: state.initialPlayMode ?? null,
			};

		default:
			return state;
	}
}
